<script setup lang="ts">
/**
 * EditUserDrawer — drawer form for `PATCH /api/cms/users/{userName}`.
 *
 * Edits the legacy financial fields (tax, minGuarantee, etc.) plus
 * the external boolean. Username is shown read-only at the top —
 * the backend doesn't allow renames (would orphan login_logs FKs).
 *
 * Empty integer inputs are sent as null, matching the legacy schema's
 * "not configured" semantics. Setting a field to 0 is distinct from
 * leaving it null and is preserved.
 */
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToastStore } from '@/stores/toast'
import Drawer from '@/components/ui/Drawer.vue'
import Button from '@/components/ui/Button.vue'
import Input from '@/components/ui/Input.vue'
import Switch from '@/components/ui/Switch.vue'
import FormRow from '@/components/ui/FormRow.vue'
import { useApiForm } from '@/composables/useApiForm'
import { userUpdateSchema } from '../schemas'
import { useUpdateUserMutation } from '../queries'
import type { UserView } from '../api'

interface Props {
  user: UserView | null
}
const props = defineProps<Props>()

const open = defineModel<boolean>('open', { required: true })

const { t } = useI18n()
const toast = useToastStore()

// We can't construct the mutation hook with a userName until we
// have one — which means we re-construct it on user changes. The
// Vue Query patterns expect this; the underlying mutation cache is
// keyed elsewhere.
const userName = computed(() => props.user?.userName ?? '')
const updateUser = useUpdateUserMutation(userName.value)

const form = useApiForm({
  schema: userUpdateSchema,
  initialValues: {
    external: props.user?.external ?? false,
    tax: props.user?.tax,
    minGuarantee: props.user?.minGuarantee,
    withholdingTax: props.user?.withholdingTax,
    detailedReport: props.user?.detailedReport,
    approve: props.user?.approve,
  },
  submit: async (values) => updateUser.mutateAsync(values),
  onSuccess: () => {
    toast.push({
      kind: 'success',
      message: t('iam.edit.success', { name: userName.value }),
    })
    open.value = false
  },
})

// Re-seed the form whenever the parent passes a different user.
// Watching props.user means the drawer reuses one mutation hook
// instance per mount but switches its bound user when needed.
watch(
  () => props.user,
  (u) => {
    if (!u) return
    form.setFieldValue('external', u.external)
    form.setFieldValue('tax', u.tax)
    form.setFieldValue('minGuarantee', u.minGuarantee)
    form.setFieldValue('withholdingTax', u.withholdingTax)
    form.setFieldValue('detailedReport', u.detailedReport)
    form.setFieldValue('approve', u.approve)
  },
  { immediate: true },
)

const [external] = form.defineField('external')
const [tax] = form.defineField('tax')
const [minGuarantee] = form.defineField('minGuarantee')
const [withholdingTax] = form.defineField('withholdingTax')
const [detailedReport] = form.defineField('detailedReport')
const [approve] = form.defineField('approve')

/**
 * Bridge between the Input's string-or-number model and the form's
 * nullable-integer type. Empty string -> null, non-numeric -> null.
 */
function makeIntegerBinding(
  ref: { value: number | null | undefined },
): { get: () => string; set: (v: string | number) => void } {
  return {
    get: () => (ref.value === null || ref.value === undefined ? '' : String(ref.value)),
    set: (v: string | number) => {
      const s = typeof v === 'number' ? String(v) : v
      if (s.trim() === '') {
        ref.value = null
        return
      }
      const n = Number.parseInt(s, 10)
      ref.value = Number.isNaN(n) ? null : n
    },
  }
}

const taxBinding = computed<string>(makeIntegerBinding(tax))
const minGuaranteeBinding = computed<string>(makeIntegerBinding(minGuarantee))
const withholdingTaxBinding = computed<string>(makeIntegerBinding(withholdingTax))
const detailedReportBinding = computed<string>(makeIntegerBinding(detailedReport))

function close(): void {
  open.value = false
}
</script>

<template>
  <Drawer
    v-model:open="open"
    :title="$t('iam.edit.title', { name: userName })"
    :subtitle="$t('iam.edit.subtitle')"
    size="md"
  >
    <form v-if="user" @submit.prevent="form.submit()">
      <!-- Read-only userName -->
      <FormRow :label="$t('iam.column.userName')" inline>
        <Input :model-value="user.userName" readonly />
      </FormRow>

      <FormRow
        :label="$t('iam.create.external.label')"
        :helper="$t('iam.create.external.helper')"
        inline
      >
        <Switch :model-value="external ?? false" @update:model-value="(v: boolean) => { external = v }" />
      </FormRow>

      <FormRow
        :label="$t('iam.column.tax')"
        :helper="$t('iam.edit.tax.helper')"
      >
        <Input
          :model-value="taxBinding"
          type="number"
          :placeholder="$t('iam.edit.tax.placeholder')"
          :error="form.errors.value.tax"
          @update:model-value="(v: string | number) => (taxBinding = String(v))"
        />
      </FormRow>

      <FormRow
        :label="$t('iam.column.minGuarantee')"
      >
        <Input
          :model-value="minGuaranteeBinding"
          type="number"
          :error="form.errors.value.minGuarantee"
          @update:model-value="(v: string | number) => (minGuaranteeBinding = String(v))"
        />
      </FormRow>

      <FormRow
        :label="$t('iam.column.withholdingTax')"
      >
        <Input
          :model-value="withholdingTaxBinding"
          type="number"
          :error="form.errors.value.withholdingTax"
          @update:model-value="(v: string | number) => (withholdingTaxBinding = String(v))"
        />
      </FormRow>

      <FormRow
        :label="$t('iam.column.detailedReport')"
      >
        <Input
          :model-value="detailedReportBinding"
          type="number"
          :error="form.errors.value.detailedReport"
          @update:model-value="(v: string | number) => (detailedReportBinding = String(v))"
        />
      </FormRow>

      <FormRow
        :label="$t('iam.column.approve')"
        inline
      >
        <Switch
          :model-value="approve === true"
          @update:model-value="(v) => (approve = v)"
        />
      </FormRow>
    </form>

    <template #footer>
      <Button kind="ghost" @click="close">
        {{ $t('actions.cancel') }}
      </Button>
      <Button
        kind="primary"
        type="submit"
        :loading="form.isSubmitting.value"
        @click="form.submit()"
      >
        {{ $t('iam.edit.submit') }}
      </Button>
    </template>
  </Drawer>
</template>
